// Fully tears down each WP Swiper instance's autoplay-driven slider while
// it's scrolled out of the viewport, and rebuilds it on scroll back in.
//
// wp-swiper's frontend bundle (wp-content/plugins/wp-swiper) is
// protected/read-only for this project, so this can't be a plugin-side
// change -- but the bundle already exposes exactly what's needed for a
// theme-side add-on without touching it: window.wpSwiper (every live
// instance), and window.wpSwiperDestroy(el) / window.wpSwiperReinit(el,
// index) -- the same pair the plugin's own editor/breakpoint code uses to
// tear down and rebuild a slider in place.
//
// Why destroy/reinit instead of a lighter pause: verified against a real
// DevTools Performance trace (not just this module's own state flags) that
// an effect:'fade' autoplay instance keeps requestAnimationFrame ticking
// at its full rate no matter which "softer" control is used --
// .autoplay.pause()/.resume() are silent no-ops once autoplay is actually
// mid-cycle (paused stays false, the rAF rate is unaffected);
// .autoplay.stop()/.start() do stop the *autoplay* timer specifically, but
// a separate, autoplay-independent rAF loop inside the fade effect module
// keeps running regardless -- confirmed by calling .disable() (also no
// effect) and only .destroy() actually silencing it (0 calls/2s
// afterwards, vs the full rate before). This is what a steady scroll
// position resting on the events-grid, far from any slider, was
// mysteriously still paying for: ~1300ms/5s of continuous main-thread work
// with the recommended card's own glow AND every autoplay timer already
// stopped, gone entirely once the nearby fade sliders were destroyed
// instead of merely paused. .destroy()/.wpSwiperReinit() were verified
// safe for this repeated-toggle use (5 destroy/reinit cycles, slider stays
// fully functional, no console errors) -- reinit takes the same index the
// instance was originally created with, which the plugin encodes as a
// `wp-swiper--<index>` class on the .wp-swiper wrapper.
//
// IntersectionObserver alone isn't reliable here either: during a
// scroll-behavior:smooth jump (e.g. scrollIntoView() past this slider on
// the way to a different section further down), the browser can report an
// "entered" transition mid-flight without ever reporting the following
// "left again" transition once the scroll settles. A periodic
// getBoundingClientRect() sweep is the source of truth and self-corrects
// anything the observer's callback missed; the observer is kept alongside
// it only so a normal (non-animated) scroll still reacts immediately
// instead of waiting for the next sweep.
const CORRECTION_INTERVAL_MS = 1500;
// Much tighter than the 200px buffer modules/recommended-glow-viewport.js
// uses for its own cheap animation-play-state toggle: a destroy+reinit
// cycle measures ~2.7ms total (negligible, sub-frame), so there's no need
// to keep a slider's whole instance alive well before it's actually
// close -- the earlier 200px value was specifically what kept the
// adjacent Sponsors slider (1 slide, fade effect) alive while resting on
// the Upcoming Events grid section above it, reproducing the exact
// "steady-state cost near the cards, not near hero/footer" gap this was
// meant to fix.
const VIEWPORT_MARGIN_PX = 50;

const managedWrappers = new WeakSet();
const tracked = [];

const isNearViewport = (el) => {
    const rect = el.getBoundingClientRect();
    // A display:none (or otherwise unrendered) element reports an
    // all-zero rect -- (0,0,0,0) numerically satisfies "near the top of
    // the viewport" below, which would wrongly keep it rebuilt. A zero
    // width/height is never a real on-screen size, so treat it as
    // definitely not visible instead.
    if (rect.width === 0 && rect.height === 0) {
        return false;
    }
    return rect.top < window.innerHeight + VIEWPORT_MARGIN_PX && rect.bottom > -VIEWPORT_MARGIN_PX;
};

// Raises entry.knownGoodHeight (and the min-height floor derived from it) to
// whatever the wrapper's CURRENT rendered height is, but only when that's
// taller than what's already recorded -- never shrinks the floor. Called from
// every correct() invocation, alive or not, not just around destroy: a
// multi-slide autoplay instance can legitimately render at a DIFFERENT height
// per active slide (e.g. the funders slider's own content: one slide's
// wp-block-gallery wraps to two rows, ~270px, another fits in one, ~200px) --
// confirmed live, this shows up as a real shrink-then-grow height oscillation
// as autoplay cycles, entirely independent of this module's own destroy/
// reinit management (the instance stays alive/unmanaged the whole time). The
// same scroll-anchor mechanism documented below applies just as much to that
// oscillation as to a destroy/reinit transition, so the tallest slide's
// height needs to become the floor as soon as it's ever observed, not only
// once per destroy cycle.
const raiseKnownGoodHeight = (entry) => {
    const currentHeight = entry.wrapper.getBoundingClientRect().height;
    if (currentHeight > entry.knownGoodHeight) {
        entry.knownGoodHeight = currentHeight;
        entry.wrapper.style.minHeight = `${entry.knownGoodHeight}px`;
    }
};

const correct = (entry) => {
    raiseKnownGoodHeight(entry);
    const shouldRun = isNearViewport(entry.wrapper);
    if (shouldRun && !entry.alive) {
        window.wpSwiperReinit?.(entry.wrapper, entry.index);
        entry.alive = true;
        raiseKnownGoodHeight(entry);
    } else if (!shouldRun && entry.alive) {
        // wp-swiper's own fade-effect layout (combined with this theme's
        // `.swiper-wrapper { height: auto !important }` override, see
        // _logo-slider.scss) can render the wrapper at a DIFFERENT height while
        // a slider is alive/managed than its raw/settled CSS content height once
        // destroyed -- confirmed live on the sponsors slider: alive ~149px,
        // destroyed ~200px (matching `.wp-block-gallery img { max-height:
        // 200px }`), with getBoundingClientRect() read immediately before vs.
        // immediately after destroy() showing the difference directly, no
        // further change afterwards. The reverse also holds: reinit-ing an
        // already-destroyed wrapper can immediately collapse or grow it back to
        // whatever the alive layout wants, within a single frame -- and, for a
        // multi-slide autoplay instance, EACH slide can independently want a
        // different alive height too (confirmed live on the funders slider:
        // one slide's wp-block-gallery wraps to two rows, ~270px, another fits
        // in one, ~200px). If any such transition happens while the wrapper is
        // at or near the user's current scroll position (a scroll-triggered
        // correct() call, by definition, or an autoplay cycle firing while the
        // section is already in view), the browser's own scroll-anchoring
        // silently adjusts scrollY to compensate for the layout shift -- an
        // involuntary extra scroll the user never asked for. That is this
        // bug's actual mechanism ("page gets stuck, then jitters if you stop
        // scrolling at a certain spot"), not a touch-event/CSS issue.
        //
        // raiseKnownGoodHeight() above already re-measures on every correct()
        // call (alive or not) and keeps min-height locked to the tallest height
        // ever observed across ALL of: initial attach, every destroy, every
        // reinit, and the periodic correction sweep's own idle re-checks while
        // alive -- so a later transition to a shorter state, whichever kind
        // caused it, can no longer render below that floor. destroy() itself
        // only needs to hand off which slider is torn down; raiseKnownGoodHeight
        // is what keeps the floor accurate on both sides of that call.
        window.wpSwiperDestroy?.(entry.wrapper);
        entry.alive = false;
        raiseKnownGoodHeight(entry);
    }
};

const attach = (instance) => {
    // Only instances actually configured with autoplay enabled have
    // anything worth tearing down -- wp-swiper always normalizes
    // params.autoplay to an object (with its own `enabled` flag) even when
    // a slider is manually-navigated-only, so checking for the object's
    // presence alone would manage every slider on the page for no reason.
    if (!instance?.params?.autoplay?.enabled || !instance.el) {
        return;
    }
    const wrapper = instance.el.closest('.wp-swiper');
    if (!wrapper || managedWrappers.has(wrapper)) {
        return;
    }
    const indexMatch = wrapper.className.match(/wp-swiper--(\d+)/);
    if (!indexMatch) {
        return;
    }
    managedWrappers.add(wrapper);
    const observer = new IntersectionObserver(() => correct(entry), {
        rootMargin: `${VIEWPORT_MARGIN_PX}px 0px`,
    });
    // observer kept on the entry itself (unlike recommended-glow-canvas.js/
    // recommended-glow-viewport.js's own single SHARED observer -- this
    // module creates one dedicated IntersectionObserver per slider instead)
    // so a later prune pass (see scan()'s own doc comment) can disconnect
    // exactly this one when its wrapper goes away, instead of leaking it.
    // knownGoodHeight starts at 0 (not yet measured); the observer's own
    // near-immediate first callback is fine to let straight through to
    // correct() here -- raiseKnownGoodHeight() (see its own comment) measures
    // fresh on every call, so no extra gating/delay is needed before this
    // entry's first live evaluation.
    const entry = {
        wrapper,
        index: Number(indexMatch[1]),
        alive: true,
        observer,
        knownGoodHeight: 0,
    };
    tracked.push(entry);
    observer.observe(wrapper);
    correct(entry);
};

const scan = () => {
    // Prunes entries for wrappers a since-replaced ACF re-render already
    // removed from the DOM -- same leak shape as
    // recommended-glow-canvas.js/recommended-glow-viewport.js (see their own
    // doc comments): each entry here owns its OWN dedicated
    // IntersectionObserver (not a shared one), so disconnect() is what
    // actually releases its strong reference to a detached wrapper, on top
    // of dropping it from `tracked` so the correction interval stops
    // iterating it too.
    for (let i = tracked.length - 1; i >= 0; i--) {
        const entry = tracked[i];
        if (entry.wrapper.isConnected) {
            continue;
        }
        entry.observer.disconnect();
        tracked.splice(i, 1);
    }
    if (Array.isArray(window.wpSwiper)) {
        window.wpSwiper.forEach(attach);
    }
};

export default function initSwiperViewportAutoplay() {
    if (typeof window.IntersectionObserver !== 'function') {
        return;
    }

    scan();

    // wp-swiper initializes on DOMContentLoaded (already fired by the time
    // jQuery's $(document).ready runs this) plus a MutationObserver for
    // anything added later -- a short poll covers the gap for any instance
    // whose init is still mid-flight (e.g. a slower third-party script
    // ahead of it in the load order), without polling forever.
    let attempts = 0;
    const poll = window.setInterval(() => {
        scan();
        attempts += 1;
        if (attempts >= 20) {
            window.clearInterval(poll);
        }
    }, 500);

    if (typeof window.MutationObserver === 'function' && document.body) {
        new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
    }

    window.setInterval(() => {
        tracked.forEach(correct);
    }, CORRECTION_INTERVAL_MS);
}
